import { forwardRef, isValidElement, type ReactElement } from 'react';

import { type LBIcon } from '@/assets/icons/icons';
import { Tooltip, type TooltipProps } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

type RoundIconButtonProps = {
  icon: LBIcon | ReactElement;
  label: string;
  size?: 'xs' | 'sm' | 'md';
  active?: boolean;
  hotkey?: TooltipProps['hotkey'];
  tooltipSide?: TooltipProps['side'];
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const RoundIconButton = forwardRef<
  HTMLButtonElement,
  RoundIconButtonProps
>(
  (
    {
      icon: Icon,
      label,
      size = 'md',
      active,
      hotkey,
      tooltipSide = 'bottom',
      className,
      ...props
    },
    ref
  ) => (
    <Tooltip label={label} hotkey={hotkey} side={tooltipSide}>
      <button
        ref={ref}
        type="button"
        aria-label={label}
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full',
          'text-foreground-muted hover:bg-surface-hover hover:text-foreground',
          'data-[state=open]:bg-surface-hover data-[state=open]:text-foreground',
          'focus-ring transition-colors disabled:pointer-events-none disabled:opacity-50',
          size === 'xs' ? 'size-6' : size === 'sm' ? 'size-8' : 'size-10',
          active && 'bg-surface-active text-foreground',
          className
        )}
        {...props}
      >
        {isValidElement(Icon) ? (
          Icon
        ) : (
          <Icon
            className={cn('shrink-0', size === 'xs' ? 'size-3.5' : 'size-4')}
          />
        )}
      </button>
    </Tooltip>
  )
);
RoundIconButton.displayName = 'RoundIconButton';

export const ROUND_ICON_BUTTON_EDGE_INSET = {
  xs: '-mr-[5px]',
  sm: '-mr-2',
  md: '-mr-3',
} as const;
